import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';

const STATUS_META = {
  proposed: { label: 'Proposed', cls: 'bg-cream-100 text-ink-700' },
  active:   { label: 'Active',   cls: 'bg-olive-100 text-olive-900' },
  paused:   { label: 'Paused',   cls: 'bg-gold-500/15 text-gold-600' },
  done:     { label: 'Done',     cls: 'bg-ink-700/10 text-ink-700' },
};

export default function Projects() {
  const { isOwner } = useOutletContext();
  const [list, setList] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/projects${showAll ? '?all=1' : ''}`)
      .then(({ projects }) => setList(projects))
      .catch(e => setErr(e.message));
  }, [showAll]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!list) return <div className="text-ink-700/60 italic">Loading…</div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="" className="h-10 w-10" />
          <h1 className="text-2xl font-serif text-olive-900">Projects</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Include done
          </label>
          {isOwner && <Link to="/usda" className="btn-ghost">USDA Programs</Link>}
          {isOwner && <Link to="/projects/new" className="btn-primary">+ Add</Link>}
        </div>
      </header>

      {list.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No projects yet.</p>
          {isOwner && <Link to="/projects/new" className="btn-primary mt-4">Add first project</Link>}
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map(p => {
            const meta = STATUS_META[p.status] || STATUS_META.proposed;
            const remaining = p.budget_cents != null ? p.budget_cents - (p.spent_cents || 0) + (p.reimbursed_cents || 0) : null;
            const pct = p.budget_cents ? Math.min(100, Math.round((p.spent_cents / p.budget_cents) * 100)) : null;
            return (
              <li key={p.id} className="card">
                <Link to={`/projects/${p.id}`} className="block">
                  <div className="flex items-start gap-3">
                    <span className={`pill ${meta.cls}`}>{meta.label}</span>
                    <div className="flex-1">
                      <div className="font-medium text-ink-900">{p.name}</div>
                      {p.description && <div className="text-sm text-ink-700/70 mt-0.5">{p.description}</div>}
                      <div className="text-xs text-ink-700/60 mt-1 flex flex-wrap gap-x-3">
                        {p.owner_name && <span>Owner: <strong>{p.owner_name}</strong></span>}
                        {p.segment_name && <span>{p.segment_name}</span>}
                        {p.deadline && <span>Due {fmtDate(p.deadline)}</span>}
                        {p.usda_program_name && <span className="pill bg-rust-500/15 text-rust-700">USDA: {p.usda_program_name}</span>}
                      </div>
                      {isOwner && p.budget_cents != null && (
                        <div className="mt-2">
                          <div className="text-xs flex justify-between text-ink-700/70">
                            <span>{fmtMoney(p.spent_cents || 0)} spent / {fmtMoney(p.budget_cents)} budget</span>
                            {(p.reimbursed_cents || 0) > 0 && <span className="text-olive-700">+ {fmtMoney(p.reimbursed_cents)} USDA</span>}
                          </div>
                          {pct != null && (
                            <div className="mt-1 h-1.5 bg-cream-100 rounded-full overflow-hidden">
                              <div className={`h-full ${pct > 100 ? 'bg-rust-500' : 'bg-olive-700'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-ink-700/40">›</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
